/**
 * useUserConnections — paginated followers / following / buddies for one
 * profile, plus the relation actions the list offers.
 *
 * Wraps POST /UserProfile/GetUserConnections and the relation endpoints so
 * both platforms share the relation mapping and the invite status codes.
 *
 * The rows are never held on their own: they are committed together with the
 * key of the list they came from (`profileUserId` + `relation`), and the hook
 * reports rows only while that key is the one being asked for. Switching tabs
 * therefore cannot show the previous tab's people, and a response that arrives
 * after the member moved on cannot be committed, so the caller does not have to
 * defend against either. Superseded requests are also aborted, so a slow
 * followers page stops competing with the buddies page the member is reading.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProfile } from '../context/ProfileContext';
import profileService from '../api/services/profileService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { INVITE_STATUS, isAlreadyHandledInvite } from '../utils/buddyInvite';
import { notifyBuddyRelationshipChanged } from '../utils/buddyRelationshipEvents';
import { getTimezoneOffsetMinutes } from '../utils/dateHelpers';
import {
  EMPTY_CONNECTIONS_PAGE,
  resolveConnectionsView,
  type CommittedConnectionsPage,
  type ConnectionsFetchMode,
  type PendingConnectionsRequest,
} from './connectionsView';
import type { ApiError } from '../types/api';
import type { UserConnectionItem } from '../types/userProfile';

const PAGE_SIZE = 20;

export type ConnectionTab = 'followers' | 'following' | 'buddies' | 'blocked';
export type BuddiesSubTab = 'active' | 'requested';
export type ConnectionAction =
  | 'unfollow'
  | 'removeBuddy'
  | 'block'
  | 'unblock'
  | 'acceptRequest'
  | 'rejectRequest';

export interface ConnectionActionResult {
  success: boolean;
  message?: string;
}

interface UseUserConnectionsParams {
  profileUserId?: number | null;
  tab: ConnectionTab;
  /** Only honoured on the signed-in member's own buddies list. */
  subTab?: BuddiesSubTab;
  isOwnProfile?: boolean;
  /** Already debounced by the caller. */
  searchText?: string;
}

export interface UseUserConnectionsResult {
  /** Only ever the rows of the list currently being asked for. */
  users: UserConnectionItem[];
  /** True from the moment a list is needed until its own first page arrives. */
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  actionLoading: { userId: number; action: ConnectionAction } | null;
  loadMore: () => void;
  refresh: () => void;
  runAction: (
    user: UserConnectionItem,
    action: ConnectionAction,
  ) => Promise<ConnectionActionResult>;
}

/** The `relation` filter the API expects for each tab. */
export const resolveConnectionRelation = (
  tab: ConnectionTab,
  subTab: BuddiesSubTab,
  isOwnProfile: boolean,
): string => {
  switch (tab) {
    case 'followers':
      return 'followers';
    case 'following':
      return 'following';
    case 'blocked':
      return 'blocked';
    default:
      return isOwnProfile && subTab === 'requested' ? 'BuddyInvite' : 'Buddy';
  }
};

export const useUserConnections = ({
  profileUserId,
  tab,
  subTab = 'active',
  isOwnProfile = true,
  searchText = '',
}: UseUserConnectionsParams): UseUserConnectionsResult => {
  const { userRelation } = useProfile();
  const [committedMap, setCommittedMap] = useState<Record<string, CommittedConnectionsPage>>({});
  const [pending, setPending] = useState<PendingConnectionsRequest | null>(null);
  const [actionLoading, setActionLoading] = useState<{ userId: number, action: ConnectionAction } | null>(null);

  const [reloadToken, setReloadToken] = useState(0);

  // Users removed by an action stay out of the list even if a page that was
  // already in flight still carries them.
  const removedIdsRef = useRef<Set<number>>(new Set());
  /** The request whose response may still be committed; anything else is stale. */
  const activeRef = useRef<{ controller?: AbortController } | null>(null);

  const relation = useMemo(
    () => resolveConnectionRelation(tab, subTab, isOwnProfile),
    [tab, subTab, isOwnProfile],
  );

  const targetUserId = Number(profileUserId);
  const hasTarget = Number.isFinite(targetUserId) && targetUserId > 0;
  /**
   * What identifies the list on screen. The search term is deliberately not
   * part of it: it filters within the current tab and the callers filter
   * locally too, so a new term refines the rows already shown instead of
   * blanking them.
   */
  const requestKey = hasTarget ? `${targetUserId}|${relation}` : '';

  const fetchPage = useCallback(
    async (page: number, mode: ConnectionsFetchMode) => {
      if (!requestKey) {
        // The profile this list belongs to went away (logout, navigation): drop
        // whatever was in flight for it instead of letting it settle.
        activeRef.current?.controller?.abort();
        activeRef.current = null;
        setPending(null);
        return;
      }

      // Only one list request at a time: a newer one always wins.
      activeRef.current?.controller?.abort();
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : undefined;
      const active = { controller };
      activeRef.current = active;
      setPending({ key: requestKey, mode });

      try {
        const response = await profileService.getUserConnections(
          {
            profileUserId: targetUserId,
            relation,
            startIndex: page,
            pageSize: PAGE_SIZE,
            timezoneOffset: getTimezoneOffsetMinutes(),
            searchText: searchText || undefined,
          },
          controller?.signal,
        );
        if (activeRef.current !== active) return;

        const received = response?.data?.users ?? [];
        const incoming = received.filter(user => !removedIdsRef.current.has(user.userId));

        setCommittedMap(previousMap => {
          const previous = previousMap[requestKey] || EMPTY_CONNECTIONS_PAGE;
          const base =
            mode === 'append' ? previous.users : [];
          const seen = new Set(base.map(user => user.userId));
          return {
            ...previousMap,
            [requestKey]: {
              key: requestKey,
              users: [...base, ...incoming.filter(user => !seen.has(user.userId))],
              page,
              hasMore: received.length === PAGE_SIZE,
              error: '',
            },
          };
        });
      } catch (caught) {
        if (activeRef.current !== active || controller?.signal.aborted) return;
        setCommittedMap(previousMap => {
          const previous = previousMap[requestKey] || EMPTY_CONNECTIONS_PAGE;
          const keepRows = mode === 'append';
          return {
            ...previousMap,
            [requestKey]: {
              key: requestKey,
              users: keepRows ? previous.users : [],
              page: keepRows ? previous.page : 1,
              hasMore: false,
              error: getApiErrorMessage(caught as ApiError),
            },
          };
        });
      } finally {
        if (activeRef.current === active) {
          activeRef.current = null;
          setPending(null);
        }
      }
    },
    [relation, requestKey, searchText, targetUserId],
  );

  useEffect(() => {
    removedIdsRef.current.clear();
    fetchPage(1, 'replace').catch(() => undefined);
    return () => {
      activeRef.current?.controller?.abort();
    };
  }, [fetchPage, reloadToken]);

  const committed = committedMap[requestKey] || EMPTY_CONNECTIONS_PAGE;
  const view = resolveConnectionsView(committed, pending, requestKey);

  const loadMore = useCallback(() => {
    if (pending || !view.hasMore) return;
    fetchPage(view.page + 1, 'append').catch(() => undefined);
  }, [fetchPage, pending, view.hasMore, view.page]);

  const refresh = useCallback(() => setReloadToken(token => token + 1), []);

  const runAction = useCallback(
    async (
      user: UserConnectionItem,
      action: ConnectionAction,
    ): Promise<ConnectionActionResult> => {
      setActionLoading({ userId: user.userId, action });
      try {
        if (action === 'acceptRequest' || action === 'rejectRequest') {
          const status =
            action === 'rejectRequest'
              ? INVITE_STATUS.reject
              : INVITE_STATUS.accept;
          try {
            await profileService.updateInvite({ senderId: user.userId, status });
          } catch (caught) {
            if (!isAlreadyHandledInvite(caught)) throw caught;
          }
        } else if (action === 'block' || action === 'unblock') {
          const result = await profileService.setBlockRelation(user.userId, action === 'block');
          if (!result.success) {
            return { success: false, message: result.message };
          }
        } else {
          await userRelation(
            user.userId,
            action === 'unfollow' ? 'UnFollow' : 'RemoveBuddy',
          );
        }

        if (action === 'acceptRequest') {
          setCommittedMap(previousMap => {
            const previous = previousMap[requestKey] || EMPTY_CONNECTIONS_PAGE;
            return {
              ...previousMap,
              [requestKey]: {
                ...previous,
                users: previous.users.map(item =>
                  item.userId === user.userId
                    ? { ...item, isBuddy: tab === 'buddies', invited: false, following: 1 }
                    : item,
                ),
              },
            };
          });
        } else {
          removedIdsRef.current.add(user.userId);
          setCommittedMap(previousMap => {
            const previous = previousMap[requestKey] || EMPTY_CONNECTIONS_PAGE;
            return {
              ...previousMap,
              [requestKey]: {
                ...previous,
                users: previous.users.filter(item => item.userId !== user.userId),
              },
            };
          });
        }

        notifyBuddyRelationshipChanged({
          relatedUserId: user.userId,
          action:
            action === 'acceptRequest'
              ? 'accept'
              : action === 'rejectRequest'
                ? 'reject'
                : 'remove',
        });

        return { success: true };
      } catch (caught) {
        return { success: false, message: getApiErrorMessage(caught as ApiError) };
      } finally {
        setActionLoading(null);
      }
    },
    [tab, userRelation, requestKey],
  );

  return {
    users: view.users,
    loading: view.loading,
    loadingMore: view.loadingMore,
    hasMore: view.hasMore,
    error: view.error,
    actionLoading,
    loadMore,
    refresh,
    runAction,
  };
};

export default useUserConnections;
